import { RISK_LEVELS, RISK_SCALE_EDGE_ALPHA, riskLabel, riskVar, type RiskLevel } from "@/lib/risk";
import { cn } from "@/lib/utils";

/**
 * A level, written the way the map key writes it: a swatch of the level's colour and the
 * word beside it. Was a filled, rounded pill — fine once, but a page of coloured pills
 * is one of the surest signs of a generated interface.
 */
export function RiskBadge({ level, className }: { level: RiskLevel; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 text-sm font-semibold text-foreground",
        className,
      )}
    >
      <span
        aria-hidden
        className="inline-block h-2.5 w-2.5 shrink-0 ring-1 ring-foreground/25 [print-color-adjust:exact]"
        style={{ backgroundColor: riskVar[level] }}
      />
      {riskLabel[level]}
    </span>
  );
}

/**
 * Where a place sits on the four-step scale, so "Spreading" reads as "second of four"
 * rather than a word on its own. The text equivalent is the sentence in aria-label.
 *
 * Every step is drawn at full colour with a hairline edge, so even the palest one holds
 * its shape on the cream card. The current step is the strong one: taller, framed in ink,
 * and labelled in bold. (It used to be the other steps faded to a third, which made
 * "Rarely reported" all but vanish.)
 */
export function RiskScale({
  level,
  place,
  className,
}: {
  level: RiskLevel;
  place: string;
  className?: string;
}) {
  const at = RISK_LEVELS.indexOf(level);
  const edge = `color-mix(in oklab, var(--foreground) ${RISK_SCALE_EDGE_ALPHA * 100}%, transparent)`;
  return (
    <figure
      className={cn("w-full", className)}
      aria-label={`${place}: ${riskLabel[level]}, level ${at + 1} of ${RISK_LEVELS.length}.`}
    >
      <ol aria-hidden className="grid grid-cols-4 gap-1">
        {RISK_LEVELS.map((l, i) => {
          const current = i === at;
          return (
            <li key={l} className="min-w-0">
              <span className="flex h-3.5 items-end">
                <span
                  data-current={current || undefined}
                  className={cn(
                    "block w-full [print-color-adjust:exact] print:border print:border-black",
                    current ? "h-3.5 print:border-2" : "h-1.5",
                  )}
                  style={{
                    backgroundColor: riskVar[l],
                    boxShadow: `inset 0 0 0 ${current ? "1.5px var(--foreground)" : `1px ${edge}`}`,
                  }}
                />
              </span>
              <span
                className={cn(
                  "mt-2 block text-[0.6875rem] leading-tight sm:text-xs",
                  current ? "font-semibold text-foreground" : "text-muted-foreground",
                )}
              >
                {riskLabel[l]}
              </span>
            </li>
          );
        })}
      </ol>
    </figure>
  );
}
